import {
  Button,
  Dialog,
  Paper,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Chip,
  Divider,
  Switch,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Container } from 'reactstrap';

import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import UIContext from '../../context/UIContext';
import axios from '../../utils/axios';

const StyledTableCell = styled(TableCell)({
  color: 'white !important',
  border: 'solid 1px #dee2e6',
  fontWeight: '800',
  textAlign: 'center',
  backgroundColor: '#47A992',
  padding: 'none',
  whiteSpace: 'nowrap',
});

const FabricDefectInitialValue = {
  DefectDescription: '',
  Major: 'N',
  Active: 'yes',
};

const filterInitialValue = {
  searchTerm: '',
  Active: true,
};

const FabricDefect = () => {
  const [filterData, setFilterData] = useState(filterInitialValue);
  const [fabricDefectTableData, setFabricDefectTableData] = useState([]);
  const [fabricDefects, setFabricDefects] = useState([]);
  const [UserId, setUserId] = useState(null);

  const [initialValue, setInitialValue] = useState(FabricDefectInitialValue);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);

  // snackbar context
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);

  // api fetch data
  useEffect(() => {
    const UserId = sessionStorage.getItem('UserId');
    setUserId(UserId);
    getAllFabricDefect();
  }, []);

  const fabricDefectValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        DefectDescription: Yup.string()
          .trim()
          .min(3, ({ min }) => `Minimum ${min} characters required`)
          .max(100, ({ max }) => `Cannot exceed more than ${max} characters`)
          .transform((value) => (value ? value.toLowerCase() : value))
          .notOneOf(
            fabricDefects
              .filter((data) => data.FabricDefectId !== initialValue.FabricDefectId)
              .map((data) => data.DefectDescription?.trim()?.toLowerCase()),
            'Fabric Defect Description Already Exists'
          )
          .required('Enter Fabric Defect Description'),
        Active: Yup.string().oneOf(['yes', 'No'], 'Please select Yes or No').required('Please select Yes or No'),
      }),
    [fabricDefects, initialValue]
  );

  async function getAllFabricDefect() {
    try {
      showLoading();
      const response = await axios.get('Master/FabricDefect/GetAllFabricDefect');
      setFabricDefects(response.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  }

  async function InsertFabricDefect(values) {
    try {
      showLoading();
      await axios.post('Master/FabricDefect/AddFabricDefect', { ...values, UserId });
      getAllFabricDefect();
      openSnackBar('Fabric Defect Added Successfully!');
      // setFilterData(filterInitialValue);
      setEdit(false);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
    setOpen(false);
  }

  const UpdateFabricDefect = async (values) => {
    try {
      showLoading();
      await axios.post(`Master/FabricDefect/UpdateFabricDefect`, { ...values, UserId });
      getAllFabricDefect();
      setInitialValue(FabricDefectInitialValue);
      openSnackBar('Fabric Defect Updated Successfully!', 'warning');
      // setFilterData(filterInitialValue);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    setOpen(false);
    setEdit(false);
    hideLoading();
  };

  const DeleteFabricDefect = async () => {
    try {
      showLoading();
      await axios.delete(`Master/FabricDefect/DeleteFabricDefect/${deleteId}`);
      const newFabricDefect = fabricDefects.filter((data) => data.FabricDefectId !== deleteId);
      setFabricDefects(newFabricDefect);
      setFabricDefectTableData(newFabricDefect);
      openSnackBar('Fabric Defect Deleted Successfully!', 'warning');
      // setFilterData(filterInitialValue);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    handleCloseDelete();
    setDeleteId(null);
    hideLoading();
  };

  const handleFilterDataChange = (name, value) => {
    setFilterData({ ...filterData, [name]: value });
  };
  useEffect(() => {
    const newTableData = fabricDefects.filter(
      (data) =>
        data.DefectDescription.toLowerCase().includes(filterData.searchTerm?.toLowerCase()) &&
        data.Active?.toLowerCase()?.trim() === (filterData.Active ? 'yes' : 'no')
    );
    setPage(0);
    setFabricDefectTableData(newTableData);
  }, [fabricDefects, filterData]);

  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteId(null);
  };

  const handleCloseDialog = () => {
    setOpen(!open);
    setEdit(false);
    setInitialValue(FabricDefectInitialValue);
  };

  // pagination
  const { emptyRows, totalRows, currentPage } = useMemo(() => {
    const totalRows = fabricDefectTableData.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const currentPage = Math.max(0, Math.min(page, totalPages - 1));
    const emptyRows = rowsPerPage - Math.min(rowsPerPage, totalRows - page * rowsPerPage);

    return { emptyRows, totalRows, currentPage };
  }, [fabricDefectTableData, page, rowsPerPage]);

  return (
    <>
      <Container maxWidth={'xl'} sx={{ pt: 2 }} elevation={3}>
        {/* table header */}
        <Grid justifyContent={'space-between'} alignItems={'center'} spacing={2}>
          <Typography
            variant="h5"
            p={1}
            boxShadow={1}
            borderColor="#11235A"
            textAlign={'center'}
            borderRadius={2}
            pl={2}
            color="white"
            sx={{
              // borderRadius: 50,
              background: '#47A992',
              boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
            }}
          >
            <Typography variant="h6" color="white" className="font_style1" textAlign={'center'}>
              FABRIC DEFECT MASTER
            </Typography>
          </Typography>
        </Grid>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent={'space-between'}
          alignItems={'center'}
          spacing={2}
          my={3}
          px={3}
        >
          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Search"
            size="small"
            onChange={(e) => {
              handleFilterDataChange('searchTerm', e.target.value);
            }}
            value={filterData.searchTerm}
          />
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              Active
            </Typography>

            {/* Toggle Switch with "Yes" / "No" */}
            <FormControlLabel
              control={
                <Switch
                  checked={filterData.Active}
                  onChange={() => {
                    handleFilterDataChange('Active', !filterData.Active);
                  }}
                  color="success"
                />
              }
              label={filterData.Active ? 'Yes' : 'No'}
            />
            <Divider orientation="vertical" flexItem />
            <Button
              variant="outlined"
              color="secondary"
              sx={{ boxShadow: 1 }}
              onClick={() => setOpen(!open)}
              startIcon={<PlaylistAddIcon />}
            >
              Add Fabric Defect
            </Button>
          </Stack>
        </Stack>
        <TableContainer sx={{ borderRadius: '7px', maxHeight: '450px' }} component={Paper} elevation={2}>
          <Table className="table-bordered" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">S.No</StyledTableCell>
                <StyledTableCell align="left"> Fabric Defect</StyledTableCell>
                <StyledTableCell align="left"> Major</StyledTableCell>
                <StyledTableCell align="center">Active</StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(rowsPerPage > 0
                ? fabricDefectTableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : fabricDefectTableData
              ).map((data, i) => (
                <TableRow key={data.FabricDefectId} hover>
                  <TableCell align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                  <TableCell align="center">{data.DefectDescription}</TableCell>
                  <TableCell align="center">
                    <Chip
                      color={data.Major === 'Y' ? 'primary' : 'warning'}
                      label={data.Major === 'Y' ? 'Yes' : 'No'}
                      size="small"
                      icon={data.Major === 'Y' ? <CheckCircleOutlineIcon /> : <HighlightOffIcon />}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      color={data.Active === 'yes' ? 'success' : 'error'}
                      label={data.Active === 'yes' ? 'Yes' : 'No'}
                      size="small"
                      icon={data.Active === 'yes' ? <CheckCircleOutlineIcon /> : <HighlightOffIcon />}
                    />
                  </TableCell>

                  <TableCell align="center" sx={{ padding: '0' }}>
                    <IconButton
                      aria-label="Edit"
                      onClick={() => {
                        setEdit(true);
                        setOpen(true);
                        setInitialValue(data);
                      }}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton
                      aria-label="Delete"
                      onClick={() => {
                        setDeleteId(data.FabricDefectId);
                        setOpenDelete(true);
                      }}
                    >
                      <DeleteForeverIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={11} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={currentPage} // Use the calculated currentPage value
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0); // Reset the page when rowsPerPage changes
          }}
          mt={2}
        />
        <Grid m={2}>
          {/* add new popup form dialog box */}
          <Dialog maxWidth={'sm'} open={open} onClose={handleCloseDialog} fullWidth>
            <Container component={Paper} elevation={2} style={{ padding: '7px 14px' }}>
              <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mr={1}>
                <Typography variant="h6" textAlign={'left'} pl={2} fontWeight={600} color="primary">
                  {edit ? 'Edit' : 'Add'} Fabric Defect
                </Typography>
                <IconButton onClick={handleCloseDialog}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Stack sx={{ p: 1 }}>
                <Formik
                  validationSchema={fabricDefectValidationSchema}
                  initialValues={initialValue}
                  onSubmit={async (values, { resetForm }) => {
                    if (edit) await UpdateFabricDefect(values);
                    else await InsertFabricDefect(values);
                    resetForm();
                  }}
                >
                  {({ submitForm, values, errors, handleChange, setFieldValue, handleBlur, isSubmitting, touched }) => (
                    <Form>
                      <Grid container spacing={2} p={1} rowSpacing={3} alignItems="baseline">
                        <Grid item width={'100%'}>
                          <TextField
                            required
                            fullWidth
                            size="small"
                            label="Fabric Defect Description"
                            type="text"
                            name="DefectDescription"
                            onBlur={handleBlur}
                            error={!!errors.DefectDescription && touched.DefectDescription}
                            helperText={
                              errors.DefectDescription && touched.DefectDescription ? errors.DefectDescription : ''
                            }
                            value={values.DefectDescription}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item>
                          <FormControl size="small" fullWidth component="fieldset">
                            <FormLabel component="legend">Active Status</FormLabel>
                            <RadioGroup row value={values.Active?.trim()} name="Active" onChange={handleChange}>
                              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                              <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                            {errors.Active && (
                              <Typography
                                variant="caption"
                                color="error"
                                className={errors.Active ? 'shake-helper-text' : ''}
                              >
                                {errors.Active}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Checkbox
                                name="Major"
                                checked={values.Major === 'Y'}
                                onChange={(e) => {
                                  setFieldValue('Major', e.target.checked ? 'Y' : 'N');
                                }}
                                onBlur={handleBlur}
                              />
                            }
                            label="Major"
                          />
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="center" gap={2} sx={{ textAlign: 'center', mt: 3 }}>
                        <Button
                          onClick={submitForm}
                          disabled={isSubmitting}
                          fullWidth
                          variant="contained"
                          color={edit ? 'warning' : 'primary'}
                          size="small"
                          sx={{ boxShadow: 1 }}
                        >
                          {edit ? 'Update' : 'Save'}
                        </Button>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              </Stack>
            </Container>
          </Dialog>
        </Grid>
        {/* confirmation dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete} fullScreen={fullScreen} fullWidth maxWidth="xs">
          <DialogTitle id="responsive-dialog-title">Are you sure you want to delete?</DialogTitle>
          <DialogActions>
            <Button variant="contained" autoFocus onClick={handleCloseDelete} color="primary">
              Cancel
            </Button>
            <Button variant="contained" onClick={DeleteFabricDefect} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default FabricDefect;
